import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Scale } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { HeadlineOverlay, HeadlineOverlayVariation } from '@/store/videoFeatures/types/headline-overlay';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';
import { HeadlineABConfirmModal } from './HeadlineABConfirmModal';

interface HeadlineABTrafficStepProps {
  variations: HeadlineOverlayVariation[];
  headlineOverlay: HeadlineOverlay;
  onUpdateConfig: (updates: Partial<HeadlineOverlay['abTestConfig']>) => void;
  getTrafficPercentage: (variationId: string) => number;
  onActivate: () => void;
}

export function HeadlineABTrafficStep({
  variations,
  headlineOverlay,
  onUpdateConfig,
  getTrafficPercentage,
  onActivate
}: HeadlineABTrafficStepProps) {
  useLanguage();
  const [distribution, setDistribution] = useState<number[]>([]);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [draggingIndex, setDraggingIndex] = useState<number | null>(null);
  const sliderRefs = useRef<(HTMLDivElement | null)[]>([]);

  // Initialize distribution
  useEffect(() => {
    if (variations.length === 0) return;
    const initial = variations.map(v => getTrafficPercentage(v.id));
    setDistribution(initial);
  }, [variations, getTrafficPercentage]);

  const total = distribution.reduce((a, b) => a + b, 0);
  const isValid = total === 100;

  // Slider ISOLADO - não redistribui automaticamente
  const handleSliderChange = useCallback((index: number, newValue: number) => {
    const clampedValue = Math.max(0, Math.min(100, newValue));
    
    const newDistribution = [...distribution];
    newDistribution[index] = clampedValue;
    
    setDistribution(newDistribution);
  }, [distribution]);

  // Drag contínuo para o slider
  const handleSliderMouseDown = useCallback((index: number, e: React.MouseEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDraggingIndex(index);
    
    const sliderRef = sliderRefs.current[index];
    if (!sliderRef) return;
    
    const rect = sliderRef.getBoundingClientRect();
    const percentage = Math.round(((e.clientX - rect.left) / rect.width) * 100);
    handleSliderChange(index, percentage);
    
    const handleMouseMove = (moveEvent: MouseEvent) => {
      const newPercentage = Math.round(((moveEvent.clientX - rect.left) / rect.width) * 100);
      handleSliderChange(index, Math.max(0, Math.min(100, newPercentage)));
    };
    
    const handleMouseUp = () => {
      setDraggingIndex(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
    
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [handleSliderChange]);

  const handleDivideEqually = () => {
    const equalShare = Math.floor(100 / variations.length);
    const remainder = 100 - (equalShare * variations.length);
    const newDistribution = variations.map((_, i) => i === 0 ? equalShare + remainder : equalShare);
    setDistribution(newDistribution);
    toast.success(translateInterfaceText("Tráfego dividido igualmente"));
  };

  const handleActivateClick = () => {
    if (!isValid) {
      toast.error(translateInterfaceText("A distribuição precisa somar 100%"));
      return;
    }
    setShowConfirmModal(true);
  };

  const handleConfirmActivate = () => {
    setShowConfirmModal(false);
    onActivate();
  };

  return (
    <div className="space-y-8">
      {/* Block 1: Traffic Distribution */}
      <div className="space-y-4">
        <div className="flex items-center justify-end">
          <Badge className={cn(
            "tabular-nums px-3 py-1 rounded-full text-sm font-medium transition-colors",
            total === 100 && "bg-primary/10 text-primary",
            total > 100 && "bg-destructive/10 text-destructive",
            total < 100 && "bg-amber-100 dark:bg-amber-900/20 text-amber-600 dark:text-amber-400"
          )}>
            {translateInterfaceText("Total:")}{" "}{total}%
          </Badge>
        </div>

        {/* Quick Action - Dividir igualmente (discreto) */}
        <Button 
          variant="ghost" 
          size="sm" 
          onClick={handleDivideEqually}
          className="gap-1.5 text-muted-foreground hover:text-primary hover:bg-transparent h-8 px-2.5 transition-colors"
        >
          <Scale className="h-3.5 w-3.5" />
          <span className="text-xs">{translateInterfaceText("Dividir igualmente")}</span>
        </Button>

        {/* Distribution Sliders - Design Premium */}
        <div className="space-y-5 p-4 rounded-xl border border-border bg-card/50">
          {variations.map((variation, index) => (
            <div key={variation.id} className="space-y-3">
              {/* Label linha: Variação A ... XX% */}
              <div className="flex items-center justify-between">
                <span className="text-sm text-muted-foreground">
                  {translateInterfaceText("Variação")}{" "}{String.fromCharCode(65 + index)}
                </span>
                <span className="text-sm font-semibold text-foreground tabular-nums">
                  {distribution[index] ?? 0}%
                </span>
              </div>

              {/* Slider Premium - Barra fina 4px com thumb e área clicável expandida */}
              <div className="relative h-8 -my-2.5 flex items-center">
                <div 
                  ref={el => sliderRefs.current[index] = el}
                  className={cn(
                    "relative h-1 w-full bg-[#E6EBF2] rounded-full overflow-visible",
                    draggingIndex === index ? "cursor-grabbing" : "cursor-grab"
                  )}
                  onMouseDown={(e) => handleSliderMouseDown(index, e)}
                >
                  {/* Barra preenchida */}
                  <div 
                    className={cn(
                      "absolute inset-y-0 left-0 rounded-full bg-primary transition-all",
                      draggingIndex === index ? "duration-0" : "duration-150"
                    )}
                    style={{ width: `${distribution[index] ?? 0}%` }}
                  />
                  {/* Thumb circular */}
                  <div 
                    className={cn(
                      "absolute top-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 border-primary shadow-[0_2px_6px_rgba(0,0,0,0.15)] transition-all",
                      draggingIndex === index 
                        ? "scale-110 duration-0" 
                        : "duration-150 hover:scale-110 hover:shadow-[0_2px_8px_rgba(0,0,0,0.2)]"
                    )}
                    style={{ left: `calc(${distribution[index] ?? 0}% - 6px)` }}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Block 2: Duration */}
      <div className="space-y-4 border-t border-border pt-6">
        <span className="text-sm font-medium text-foreground">{translateInterfaceText("Duração do Teste")}</span>

        <div className="space-y-4">
          <label className="flex items-start gap-3">
            <input
              type="checkbox"
              checked={true}
              readOnly
              className="mt-0.5 h-4 w-4 text-primary border-border accent-primary"
            />
            <div>
              <span className="text-sm font-medium text-foreground">
                {translateInterfaceText("Sem término (manual)")}</span>
              <p className="text-[13px] text-muted-foreground mt-0.5">
                {translateInterfaceText("Você encerra quando quiser.")}</p>
            </div>
          </label>
        </div>
      </div>

      {/* Confirm Modal */}
      <HeadlineABConfirmModal
        open={showConfirmModal}
        onClose={() => setShowConfirmModal(false)}
        onConfirm={handleConfirmActivate}
        variations={variations}
        distribution={distribution}
      />
    </div>
  );
}
